import {
    Difference,
    DifferenceOutlined,
    Pageview,
    PageviewOutlined,
    QuestionMark,
    Refresh,
    Start,
    Terminal,
    Update,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useQuery } from '@tanstack/react-query';
import { diffJson } from 'diff';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { getDocumentAudit } from '../../api/admin/audit';
import { DocumentType } from '../../api/dto';
import { getDocumentTypeName } from '../../lib/records/documents/document';
import { ImpactFormattedDate } from '../ImpactFormattedDate';

interface AuditDataProps {
    documentType: DocumentType;
    documentId: number;
    open: boolean;
    onClose: () => void;
}

const getAuditEntryIcon = (type: string) => {
    switch (type) {
        case 'INITIAL':
            return <Start />;
        case 'UPDATE':
            return <Update />;
        case 'TERMINAL':
            return <Terminal />;
        default:
            return <QuestionMark />;
    }
};

export const AuditData = ({ documentType, documentId, open, onClose }: AuditDataProps) => {
    const intl = useIntl();
    const [selected, setSelected] = React.useState<number | null>(null);
    const [diffEnabled, setDiffEnabled] = React.useState(true);

    const {
        data: auditDataEntries,
        isLoading: isAuditLoading,
        refetch: refetchAuditDataEntries,
        isRefetching,
    } = useQuery(['audit', documentType, documentId], () => getDocumentAudit({ documentType, documentId }), {
        onError: () => {
            toast.error(intl.formatMessage({ id: 'common.error' }));
        },
    });

    const handleToggle = (index: number) => () => {
        setSelected(selected === null || selected !== index ? index : null);
    };

    const hasSelected = selected !== null;

    const selectedDiff = useMemo(() => {
        if (auditDataEntries && selected !== null) {
            const previous =
                selected < auditDataEntries.length - 1 ? (auditDataEntries[selected + 1].state as object) : {};
            const current = auditDataEntries[selected].state as object;
            return diffJson(previous, current);
        } else {
            return null;
        }
    }, [auditDataEntries, selected]);

    return (
        <>
            <Dialog open={open} fullScreen onClose={onClose}>
                <DialogTitle id="scroll-dialog-title">
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h6">{`Audit: ${getDocumentTypeName(
                            documentType,
                            intl
                        )} - ${documentId}`}</Typography>
                        <Stack direction="row" spacing={1}>
                            <IconButton
                                onClick={() => setDiffEnabled(!diffEnabled)}
                                disabled={isRefetching}
                                color={diffEnabled ? 'primary' : 'default'}
                            >
                                {diffEnabled ? <Difference /> : <DifferenceOutlined />}
                            </IconButton>
                            <IconButton onClick={() => refetchAuditDataEntries()} disabled={isRefetching}>
                                <Refresh />
                            </IconButton>
                        </Stack>
                    </Stack>
                </DialogTitle>
                {isAuditLoading || isRefetching ? (
                    <Box sx={{ height: '100%' }}>
                        <LinearProgress />
                    </Box>
                ) : (
                    <>
                        <Divider />
                        <Box display="flex" overflow="hidden" height="100%">
                            <Box sx={{ flexBasis: hasSelected ? '50%' : '100%', overflow: 'auto' }}>
                                <List dense>
                                    {/* any type for auditEntry is used on purpose to save time since this is an admin only feature currently */}
                                    {auditDataEntries?.map((auditEntry, index) => (
                                        <ListItemButton key={auditEntry.version} onClick={handleToggle(index)} dense>
                                            <ListItem
                                                alignItems="flex-start"
                                                secondaryAction={
                                                    <Checkbox
                                                        edge="end"
                                                        icon={<PageviewOutlined />}
                                                        checkedIcon={<Pageview />}
                                                        onChange={handleToggle(index)}
                                                        checked={selected === index}
                                                        color="primary"
                                                    />
                                                }
                                                disablePadding
                                            >
                                                <ListItemAvatar>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: selected === index ? 'primary.main' : undefined,
                                                        }}
                                                    >
                                                        {getAuditEntryIcon(auditEntry.type)}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography component="span">
                                                            {`${auditEntry.type} [${auditEntry.version}]`}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <>
                                                            <Typography variant="body2" component="span">
                                                                <ImpactFormattedDate
                                                                    date={auditEntry.commitMetadata.commitDate}
                                                                    time
                                                                />
                                                                {' by '}
                                                                <Typography component="span" variant="subtitle2">
                                                                    {auditEntry.commitMetadata.author}
                                                                </Typography>
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                            {index < auditDataEntries.length - 1 && <Divider component="li" />}
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Box>

                            {hasSelected && selectedDiff !== null && auditDataEntries && (
                                <Box
                                    sx={{
                                        flexBasis: hasSelected ? '50%' : '100%',
                                        overflow: 'auto',
                                        background: grey[200],
                                        p: 1,
                                    }}
                                >
                                    <pre>
                                        <code>
                                            {diffEnabled
                                                ? selectedDiff.map((o, i) => (
                                                      <span
                                                          key={i}
                                                          style={{
                                                              color: o.added ? 'green' : o.removed ? 'red' : undefined,
                                                          }}
                                                      >
                                                          {o.value}
                                                      </span>
                                                  ))
                                                : JSON.stringify(auditDataEntries[selected].state, null, 2)}
                                        </code>
                                    </pre>
                                </Box>
                            )}
                        </Box>
                        <Divider />
                    </>
                )}

                <DialogActions>
                    <Button onClick={onClose}>
                        <FormattedMessage id="common.close" />
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
